import React, { useEffect } from "react";
import { MdEmail, MdMessage, MdPhone } from "react-icons/md";
import { FiX } from "react-icons/fi";
import { FaUserTie } from "react-icons/fa";

import {
  cn,
  getMemberFullName,
  sanitizeBio,
  toAbsoluteTeamPhoto,
} from "./teamUtils";

const FALLBACK_AVATAR = "/img/user_avatar.jpg";

function ActionButton({ icon: Icon, label, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="
        inline-flex h-11 w-11 items-center justify-center
        rounded-full border border-slate-200
        bg-white text-slate-700 shadow-sm
        transition duration-200
        hover:-translate-y-0.5
        hover:border-orange-300
        hover:bg-orange-50
        hover:text-orange-600
        hover:shadow-md
        focus:outline-none
        focus:ring-4 focus:ring-orange-500/20

        dark:border-white/10
        dark:bg-white/5
        dark:text-slate-200
        dark:hover:border-orange-500/40
        dark:hover:bg-orange-500/10
        dark:hover:text-orange-400
      "
      title={label}
      aria-label={label}
    >
      <Icon className="text-xl" />
    </button>
  );
}

export default function TeamProfileModal({ member, onClose }) {
  const photo = toAbsoluteTeamPhoto(member?.photoURL) || FALLBACK_AVATAR;
  const fullName = getMemberFullName(member);
  const safeBio = sanitizeBio(member?.bio_member);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", onKeyDown);

    return () => {
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [onClose]);

  const call = () => {
    if (!member?.tel_member) return;

    window.location.href = `tel:${member.tel_member}`;
  };

  const sms = () => {
    if (!member?.tel_member) return;

    window.location.href = `sms:${member.tel_member}`;
  };

  const email = () => {
    if (!member?.mail_member) return;

    window.location.href = `mailto:${member.mail_member}`;
  };

  const handleImageError = (event) => {
    if (!event?.currentTarget) return;

    event.currentTarget.onerror = null;
    event.currentTarget.src = FALLBACK_AVATAR;
  };

  if (!member) return null;

  return (
    <div
      className="
        fixed inset-0 z-[80]
        flex items-center justify-center
        bg-slate-950/75 px-3 py-6
        backdrop-blur-sm
      "
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="team-profile-modal-title"
    >
      <div
        className="
          relative max-h-[92vh] w-full max-w-4xl
          overflow-hidden rounded-[2rem]
          border border-slate-200
          bg-white shadow-2xl
          dark:border-white/10
          dark:bg-slate-950
        "
        onClick={(event) => event.stopPropagation()}
      >
        {/* Bouton fermer */}
        <button
          type="button"
          onClick={onClose}
          className="
            absolute right-4 top-4 z-20
            flex h-11 w-11 items-center justify-center
            rounded-full border border-slate-200
            bg-white/90 text-slate-700
            shadow-lg backdrop-blur
            transition duration-200
            hover:rotate-90
            hover:border-orange-300
            hover:bg-orange-50
            hover:text-orange-600
            focus:outline-none
            focus:ring-4 focus:ring-orange-500/20

            dark:border-white/10
            dark:bg-slate-900/90
            dark:text-slate-200
            dark:hover:border-orange-500/40
            dark:hover:bg-orange-500/10
            dark:hover:text-orange-400
          "
          aria-label="Fermer"
        >
          <FiX className="text-xl" />
        </button>

        <div className="max-h-[92vh] overflow-y-auto overflow-x-hidden">
          <div className="grid lg:grid-cols-[0.78fr_1.22fr]">
            {/* Photo */}
            <div className="relative min-h-[320px] overflow-hidden lg:min-h-full">
              <img
                src={photo}
                alt={fullName || "Membre du club"}
                className="h-full w-full object-cover"
                loading="lazy"
                decoding="async"
                onError={handleImageError}
              />

              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-slate-950/90 via-slate-950/20 to-transparent" />

              <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-orange-500/20" />

              <div className="absolute bottom-6 left-6 right-6">
                <p
                  className="
                    inline-flex rounded-full
                    border border-white/20
                    bg-slate-950/30 px-4 py-2
                    text-xs font-bold uppercase
                    tracking-[0.18em] text-white
                    shadow-lg backdrop-blur-xl
                  "
                >
                  {member?.categorie || "Membre"}
                </p>

                <h3 className="mt-4 font-barlow text-4xl font-extrabold uppercase leading-none text-white drop-shadow-xl">
                  {member?.nom}
                </h3>

                <p className="font-barlow text-3xl font-bold leading-tight text-white/90">
                  {member?.prenom}
                </p>
              </div>
            </div>

            {/* Informations */}
            <div className="relative overflow-x-hidden p-6 text-slate-900 dark:text-white sm:p-8 lg:p-10">
              <div
                className="
                  pointer-events-none absolute -right-20 -top-20
                  h-52 w-52 rounded-full
                  bg-orange-500/10 blur-3xl
                "
              />

              <div className="relative pr-12">
                {member?.fonction && (
                  <div
                    className="
                      inline-flex items-center gap-2
                      rounded-full border border-orange-200
                      bg-orange-50 px-4 py-2
                      text-xs font-bold uppercase
                      tracking-[0.18em] text-orange-700

                      dark:border-orange-500/20
                      dark:bg-orange-500/10
                      dark:text-orange-300
                    "
                  >
                    <FaUserTie className="text-orange-600 dark:text-orange-400" />
                    {member.fonction}
                  </div>
                )}

                <h4
                  id="team-profile-modal-title"
                  className="mt-6 font-barlow text-3xl font-extrabold text-slate-950 dark:text-white"
                >
                  Profil du membre
                </h4>

                {/* Actions */}
                <div className="mt-6 flex flex-wrap gap-3">
                  {member?.tel_member && (
                    <>
                      <ActionButton
                        icon={MdPhone}
                        label="Appeler"
                        onClick={call}
                      />

                      <ActionButton
                        icon={MdMessage}
                        label="Envoyer un SMS"
                        onClick={sms}
                      />
                    </>
                  )}

                  {member?.mail_member && (
                    <ActionButton
                      icon={MdEmail}
                      label="Envoyer un email"
                      onClick={email}
                    />
                  )}
                </div>

                <div className="mt-7 space-y-4">
                  {/* Email */}
                  {member?.mail_member && (
                    <div
                      className="
                        rounded-3xl border border-slate-200
                        bg-slate-50 p-4
                        transition duration-200
                        hover:border-orange-200
                        hover:bg-orange-50/40

                        dark:border-white/10
                        dark:bg-white/5
                        dark:hover:border-orange-500/30
                        dark:hover:bg-orange-500/5
                      "
                    >
                      <div className="flex items-center gap-3">
                        <span
                          className="
                            flex h-10 w-10 shrink-0
                            items-center justify-center
                            rounded-2xl
                            bg-orange-100 text-orange-600

                            dark:bg-orange-500/10
                            dark:text-orange-400
                          "
                        >
                          <MdEmail className="text-xl" />
                        </span>

                        <div className="min-w-0">
                          <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-300">
                            Email
                          </p>

                          <a
                            href={`mailto:${member.mail_member}`}
                            className="
                              mt-1 block break-all
                              text-sm font-bold
                              text-orange-600
                              transition hover:text-orange-700
                              hover:underline

                              dark:text-orange-400
                              dark:hover:text-orange-300
                            "
                          >
                            {member.mail_member}
                          </a>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Téléphone */}
                  {member?.tel_member && (
                    <div
                      className="
                        rounded-3xl border border-slate-200
                        bg-slate-50 p-4
                        transition duration-200
                        hover:border-orange-200
                        hover:bg-orange-50/40

                        dark:border-white/10
                        dark:bg-white/5
                        dark:hover:border-orange-500/30
                        dark:hover:bg-orange-500/5
                      "
                    >
                      <div className="flex items-center gap-3">
                        <span
                          className="
                            flex h-10 w-10 shrink-0
                            items-center justify-center
                            rounded-2xl
                            bg-orange-100 text-orange-600

                            dark:bg-orange-500/10
                            dark:text-orange-400
                          "
                        >
                          <MdPhone className="text-xl" />
                        </span>

                        <div>
                          <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-300">
                            Téléphone
                          </p>

                          <a
                            href={`tel:${member.tel_member}`}
                            className="
                              mt-1 block text-sm font-bold
                              text-slate-700 transition
                              hover:text-orange-600
                              hover:underline

                              dark:text-slate-200
                              dark:hover:text-orange-400
                            "
                          >
                            {member.tel_member}
                          </a>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Bio */}
                  {member?.bio_member && (
                    <div
                      className="
                        rounded-3xl border border-slate-200
                        bg-slate-50 p-5

                        dark:border-white/10
                        dark:bg-white/5
                      "
                    >
                      <div className="mb-4 flex items-center gap-3">
                        <span
                          className="
                            flex h-10 w-10 shrink-0
                            items-center justify-center
                            rounded-2xl
                            bg-orange-100 text-orange-600

                            dark:bg-orange-500/10
                            dark:text-orange-400
                          "
                        >
                          <FaUserTie />
                        </span>

                        <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-300">
                          Biographie
                        </p>
                      </div>

                      <div
                        className={cn(
                          "prose prose-slate max-w-none dark:prose-invert",
                          "text-sm leading-7 text-slate-600 dark:text-slate-300",

                          "prose-p:my-3",
                          "prose-li:my-1",

                          "prose-a:font-bold",
                          "prose-a:text-orange-600",
                          "hover:prose-a:text-orange-700",

                          "dark:prose-a:text-orange-400",
                          "dark:hover:prose-a:text-orange-300",

                          "prose-strong:text-slate-950",
                          "dark:prose-strong:!text-white",

                          "prose-b:text-slate-950",
                          "dark:prose-b:!text-white"
                        )}
                        dangerouslySetInnerHTML={{
                          __html: safeBio,
                        }}
                      />
                    </div>
                  )}
                </div>

                {/* Bouton fermer */}
                <div className="mt-8">
                  <button
                    type="button"
                    onClick={onClose}
                    className="
                      inline-flex items-center justify-center
                      rounded-full border border-orange-600
                      bg-orange-600 px-6 py-3
                      text-sm font-bold text-white
                      shadow-lg transition duration-200
                      hover:-translate-y-0.5
                      hover:border-orange-700
                      hover:bg-orange-700
                      hover:shadow-xl
                      focus:outline-none
                      focus:ring-4 focus:ring-orange-500/25

                      dark:border-orange-500
                      dark:bg-orange-500
                      dark:hover:border-orange-400
                      dark:hover:bg-orange-400
                    "
                  >
                    Fermer
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}